import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TableSortLabel,
  Box,
  Tooltip,
} from "@mui/material";
import {
  getComparator,
  stableSort,
} from "../../../common-utilities/sortingUtils";

const TrackLabOrdersTable = ({ labOrdersData, count, onChange }) => {
  const columns = [
    "Order#",
    "BO#",
    "Client",   
    "Description",
    "Shipping Date",
    "Shipped Since",
    "Monitoring Dates",
  ];
  const columnHeaders = [
    "orderNumber",
    "bottleOrderNumber",
    "clientName",
    "projectCategory",
    "shippingDate",
    "daysSinceShipped",
    "minMonitorStart",
  ];
  const rowsPerPageOptions = [20, 50, 100, 150, 200, 400, 600, 800, 1000];

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);
  useState(false);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");

  useEffect(() => {
    handleChange();
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "2-digit", day: "2-digit" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleChange = () => {
    onChange(page, rowsPerPage);
  };

  const handleViewBottleOrderClick = (bottleOrderNumber) => {
    const bottleOrderUrl = `/vlp/bottle-orders/${bottleOrderNumber}`;
    if (window.bottleOrderViewWindow && !window.bottleOrderViewWindow.closed) {
      window.bottleOrderViewWindow.location.href = bottleOrderUrl;
    } else {
      window.bottleOrderViewWindow = window.open(
        bottleOrderUrl,
        "_blank",
        "resizable=yes,scrollbars=yes"
      );
    }
  };

  const handleViewClick = (orderNumber) => {
    const orderUrl = `/vlp/lab-orders/${orderNumber}`;
    if (window.labOrderViewWindow && !window.labOrderViewWindow.closed) {
      window.labOrderViewWindow.location.href = orderUrl;
    } else {
      window.labOrderViewWindow = window.open(
        orderUrl,
        "_blank",
        "resizable=yes,scrollbars=yes"
      );
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = stableSort(labOrdersData, getComparator(order, orderBy));

  const calculateDaysSinceShipped = (shippingDate) => {
    if (!shippingDate) return "N/A"; // Handle null case
    const shippedDate = new Date(shippingDate);
    const today = new Date();
    const diffTime = Math.abs(today - shippedDate);
    return Math.floor(diffTime / (1000 * 60 * 60 * 24)); // Convert to days
  };

  return (
    <>
      <Paper style={{ marginRight: "20px" }}>
        <TableContainer
          sx={{ maxHeight: 500, overflowY: "auto", width: "99%" }}
        >
          <Table dense stickyHeader>
            <TableHead
              sx={{
                backgroundColor: (theme) => theme.palette.primary.main,
                color: "white",
              }}
            >
              <TableRow>
                {columns.map((column, index) => (
                  <TableCell
                    key={column}
                    onClick={() => handleRequestSort(columnHeaders[index])}
                  >
                    <TableSortLabel
                      active={orderBy === columnHeaders[index]}
                      direction={
                        orderBy === columnHeaders[index] ? order : "asc"
                      }
                    >
                      {column}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((order, index) => (
                <TableRow
                  key={order.orderNumber}
                  sx={{
                    backgroundColor:
                      index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                  }}
                >
                  {columnHeaders.map((column) => (
                    <TableCell
                      key={column}
                      sx={{
                        ...(column === "orderNumber" ||
                          column === "bottleOrderNumber"
                          ? {
                            cursor: "pointer",
                            color: "blue",
                            textDecoration: "underline",
                          }
                          : {}),
                      }}
                      onClick={
                        column === "orderNumber"
                          ? () => handleViewClick(order.orderNumber)
                          : column === "bottleOrderNumber"
                            ? () =>
                              handleViewBottleOrderClick(
                                order.bottleOrderNumber
                              )
                            : undefined
                      }
                    >
                      {column === "minMonitorStart" ? (
                        <>
                          {formatDate(order.minMonitorStart)} -{" "}
                          {order.maxMonitorStart
                            ? formatDate(order.maxMonitorStart)
                            : "N/A"}
                        </>
                      ) : column === "shippingDate" ? (
                        formatDate(order[column])
                      ) : column === "daysSinceShipped" ? (
                        calculateDaysSinceShipped(order.shippingDate)
                      ) : column === "projectCategory" || column === "clientName" ? (
                        <Tooltip title={order[column]} arrow>
                          <span>
                            {order[column].length > 15
                              ? `${order[column].slice(0, 15)}...`
                              : order[column]}
                          </span>
                        </Tooltip>
                      ) : (
                        order[column]
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </>
  );
};

export default TrackLabOrdersTable;
